import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { Marquee } from "../components/Marquee";
import { IMAGES } from "../data/site";

const CHAPTERS = [
  {
    num: "01",
    title: "Story. Scene. Sound.",
    copy: "Every project lives in three chapters: the strategy and brand (Story), the content that makes it visible (Scene), and the technical craft that delivers it (Sound). Most companies sell you one chapter. We wrote the whole book.",
  },
  {
    num: "02",
    title: "Wisconsin rooted, on purpose.",
    copy: "We know the venues, the markets, the co-ops, and the summer date crunch. From Milwaukee to Minocqua, our work is built for the businesses and people of this state — and we plan like neighbors, not tourists.",
  },
  {
    num: "03",
    title: "Outcomes over equipment.",
    copy: "Nobody ever grew a business because of a camera model. We talk about applicants, sales, awareness, and attendance — then choose the tools that get there. The gear is excellent. It's just not the point.",
  },
  {
    num: "04",
    title: "The right team for the project.",
    copy: "We scale to fit the brief — a rebrand doesn't need a film crew, and a commercial doesn't need a logo designer. What never changes is centralized creative direction and one point of contact, start to finish.",
  },
];

const STATS = [
  ["6", "Service areas, one roof"],
  ["40+", "Wisconsin communities served"],
  ["8", "Steps from idea to growth"],
  ["1", "Point of contact"],
];

export default function About() {
  useDocTitle("About");
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Studio Solutions"
        lines={[<>A creative partner, <span className="text-gold-dark">not a vendor list.</span></>]}
        sub="Studio Solutions is a Wisconsin creative marketing and video production company — video, content, marketing, branding, studio builds and production, carried from first idea to final delivery."
      />

      <Marquee items={["Story", "Scene", "Sound", "Branding", "Video", "Marketing", "Studio Builds", "Production"]} />

      {/* MANIFESTO */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <SectionHeading
          eyebrow="The Manifesto"
          title={<>Four things <span className="text-gold-dark">we believe.</span></>}
        />
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.07}>
              <div className="group flex h-full gap-7 border border-ink/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-xl sm:p-10">
                <p className="font-mono text-sm tracking-[0.25em] text-gold-dark">{c.num}</p>
                <div>
                  <h3 className="font-display text-2xl font-bold leading-snug tracking-tight text-ink">{c.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-smoke">{c.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="bg-night text-sand">
        <div className="mx-auto grid max-w-7xl gap-px px-4 py-20 sm:grid-cols-2 sm:px-8 sm:py-28 lg:grid-cols-4 lg:px-16">
          {STATS.map(([num, label], i) => (
            <Reveal key={label} delay={i * 0.08}>
              <div className="border-sand/10 py-6 text-center sm:border-l sm:first:border-l-0">
                <p className="font-display text-5xl font-extrabold tracking-tight text-gold sm:text-6xl">{num}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/50">{label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMPANY, NOT INDIVIDUAL */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="How We Staff"
              title={<>The right team <span className="text-gold-dark">for the project.</span></>}
              sub="Flexible and scalable — with creative direction that never changes hands."
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-sm leading-relaxed text-smoke">
                Studio Solutions brings together trusted creative and technical professionals based on
                what each project actually needs — filmmakers, photographers, editors, designers,
                production assistants and specialized crew — while creative direction and communication
                stay centralized through Studio Solutions. You get the scale of an agency bench without
                the agency runaround.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Specialists matched to each project's actual needs",
                  "Creative direction centralized through Studio Solutions",
                  "One point of contact from first call to final delivery",
                  "Scaled up or down to fit the brief — and the budget",
                ].map((t) => (
                  <li key={t} className="flex gap-4 text-sm leading-relaxed text-ink">
                    <span className="mt-1.5 block h-2 w-2 shrink-0 bg-gold" />
                    {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4">
              <MediaImg src={IMAGES.branding} alt="Brand design work" className="h-56 w-full object-cover sm:h-72" />
              <MediaImg src={IMAGES.camera} alt="Camera on a commercial shoot" className="mt-8 h-56 w-full object-cover sm:h-72" />
              <MediaImg src={IMAGES.edit} alt="Edit suite" className="h-56 w-full object-cover sm:h-72" />
              <MediaImg src={IMAGES.studioAudio} alt="Studio audio rig" className="mt-8 h-56 w-full object-cover sm:h-72" />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="Meet the crew on your project."
        sub="The fastest way to understand the difference is one short conversation about what you're planning."
      />
    </div>
  );
}
